// src/components/FilterModal/FilterModal.jsx
import { useState, useEffect, useMemo } from 'react';
import styles from './FilterModal.module.css';

const PERIOD_OPTIONS = [
  { id: 'today',     label: 'Hoje' },
  { id: 'yesterday', label: 'Ontem' },
  { id: 'last7',     label: 'Últimos 7 dias' },   // padrão novo
  { id: 'month',     label: 'Este mês' },
  { id: 'year',      label: 'Este ano' },
  { id: 'custom',    label: 'Personalizar' },
];

/** Formata para YYYY-MM-DD no fuso LOCAL (sem usar toISOString/UTC) */
function formatLocalYMD(date) {
  const d = (date instanceof Date) ? date : new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Converte input "YYYY-MM-DD" OU Date -> Date no LOCAL (meia-noite local) */
function toLocalDate(value) {
  if (!value) return new Date();
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === 'string') {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }
  return new Date();
}

export default function FilterModal({ isOpen, onClose, currentPeriod, onSave }) {
  const [tempPeriod, setTempPeriod] = useState(currentPeriod.type);
  const [startDate, setStartDate]   = useState(toLocalDate(currentPeriod.startDate || new Date()));
  const [endDate, setEndDate]       = useState(toLocalDate(currentPeriod.endDate   || new Date()));
  const [dateError, setDateError]   = useState('');

  const hasDateError = useMemo(() => {
    if (tempPeriod !== 'custom') return false;
    const isInvalid = startDate > endDate;
    setDateError(isInvalid ? 'A data inicial não pode ser maior que a data final.' : '');
    return isInvalid;
  }, [tempPeriod, startDate, endDate]);

  useEffect(() => {
    if (isOpen) {
      setTempPeriod(currentPeriod.type);
      setStartDate(toLocalDate(currentPeriod.startDate || new Date()));
      setEndDate(toLocalDate(currentPeriod.endDate || new Date()));
      setDateError('');
    }
  }, [isOpen, currentPeriod]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (tempPeriod === 'custom' && hasDateError) return;
    const payload = { type: tempPeriod };
    if (tempPeriod === 'custom') {
      payload.startDate = toLocalDate(startDate);
      payload.endDate   = toLocalDate(endDate);
    }
    onSave(payload);
  };

  const minEnd   = formatLocalYMD(startDate);
  const maxStart = formatLocalYMD(endDate);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2>Ajustar períodos</h2>
        </header>

        <div className={styles.content}>
          <fieldset className={styles.periodList}>
            {PERIOD_OPTIONS.map((option) => (
              <label key={option.id} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="period"
                  className={styles.radioInput}
                  value={option.id}
                  checked={tempPeriod === option.id}
                  onChange={() => setTempPeriod(option.id)}
                />
                <span className={styles.customRadio} />
                {option.label}
              </label>
            ))}
          </fieldset>

          {tempPeriod === 'custom' && (
            <>
              <div className={styles.customDateWrapper}>
                <div className={styles.dateInputGroup}>
                  <label htmlFor="startDate">De</label>
                  <input
                    type="date"
                    id="startDate"
                    max={maxStart}
                    value={formatLocalYMD(startDate)}
                    onChange={(e) => setStartDate(toLocalDate(e.target.value))}
                    aria-invalid={hasDateError}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label htmlFor="endDate">Até</label>
                  <input
                    type="date"
                    id="endDate"
                    min={minEnd}
                    value={formatLocalYMD(endDate)}
                    onChange={(e) => setEndDate(toLocalDate(e.target.value))}
                    aria-invalid={hasDateError}
                  />
                </div>
              </div>

              {hasDateError && (
                <div className={styles.errorBox} role="alert">
                  {dateError}
                </div>
              )}
            </>
          )}
        </div>

        <footer className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancelar</button>
          <button
            className={styles.applyBtn}
            onClick={handleSave}
            disabled={tempPeriod === 'custom' && hasDateError}
            title={tempPeriod === 'custom' && hasDateError ? dateError : 'Aplicar filtros'}
          >
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}